import { isEmpty, isEqual } from 'lodash';
import apiClient from '../../services/api-client';
import {
	TYPES,
	STATUS_ACTION_TYPES,
} from '@/views/client-connect-v2/constants.js';
import alertsService from '@/services/contacts/alerts-service.js';

const state = {
	alerts: {
		onInit: true,
		items: [],
		noAlertsFound: false,
		noResultsFound: false,
		dashboard: {
			refresh: false,
			toDoList: [],
			noToDoAlertsFound: false,
			doneForYouList: [],
			noDoneForYouAlertsFound: false,
		},
		types: {
			type: TYPES.ALERT,
			data: [],
		},
		statuses: {
			type: TYPES.STATUS,
			data: [],
		},
		dateRanges: {
			type: TYPES.DATE_RANGE,
			data: [
				{
					desc: 'Today',
					id: -1,
				},
				{
					desc: 'Previous 7 days',
					id: -7,
				},
				{
					desc: 'Previous 15 days',
					id: -15,
				},
				{
					desc: 'Previous 30 days',
					id: -30,
				},
				{
					desc: 'Next 30 days',
					id: 30,
				},
			],
			toggle: true,
		},
		totalCount: null,
		alertStatusTotals: {},
	},
	alertsView: {
		typesIds: [],
		statusesIds: [],
		dateRange: 0,
		/* this is only used in overviewModal. CCAlerts stores searchTerm in component state so when a contact
		is edited on CCAlerts page the alerts are refreshed without the search term persisting */
		searchTerm: '',
		sortColumn: '',
		sortDirection: 'asc',
		pageSize: 20,
		pageNumber: 1,
		currentFilters: null,
	},
	selectedContactsIds: [],
	selectedContactIdsUpdated: false,
};

const mutations = {
	RESET_CLIENT_CONNECT_ALERTS(state, resetCurrentFilterState) {
		state.alerts.onInit = true;
		state.alerts.items = [];
		state.alerts.totalCount = null;
		state.alertsView.typesIds = [];
		state.alertsView.statusesIds = [];
		state.alertsView.dateRange = 0;
		state.selectedContactsIds = [];
		if (resetCurrentFilterState) {
			state.alertsView.currentFilters = null;
		}
	},
	CLEAR_CLIENT_CONNECT_ALERTS(state) {
		state.alerts.items = [];
	},

	// Alerts
	GET_CLIENT_CONNECT_ALERTS_SUCCESS(state, payload) {
		if (isEmpty(payload.items)) {
			state.alerts.noAlertsFound = true;
		} else {
			state.alerts.noAlertsFound = false;
			state.alerts.noResultsFound = false;
		}
		state.alerts.items = [...state.alerts.items, ...payload.items];
		state.alerts.totalCount = payload.totalCount;
		state.alerts.onInit = false;
	},
	GET_CLIENT_CONNECT_ALERTS_BY_SEARCH_SUCCESS(state, payload) {
		if (isEmpty(payload.items)) {
			state.alerts.noResultsFound = true;
		} else {
			state.alerts.noAlertsFound = false;
			state.alerts.noResultsFound = false;
		}
		state.alerts.items = [...payload.items];
		state.alerts.totalCount = payload.totalCount;
	},
	GET_ALERT_STATUSES_SUCCESS(state, payload) {
		state.alerts.statuses.data = payload;
	},
	GET_ALERT_TYPES_SUCCESS(state, payload) {
		state.alerts.types.data = payload;
	},
	GET_DASHBOARD_TO_DO_ALERTS_SUCCESS(state, payload) {
		if (isEmpty(payload)) {
			state.alerts.dashboard.noToDoAlertsFound = true;
		} else {
			state.alerts.dashboard.noToDoAlertsFound = false;
		}
		state.alerts.dashboard.toDoList = payload;
	},
	GET_DASHBOARD_DONE_FOR_YOU_ALERTS_SUCCESS(state, payload) {
		if (isEmpty(payload)) {
			state.alerts.dashboard.noDoneForYouAlertsFound = true;
		} else {
			state.alerts.dashboard.noDoneForYouAlertsFound = false;
		}
		state.alerts.dashboard.doneForYouList = payload;
	},
	UPDATE_ALERTSVIEW_TYPESIDS(state, payload) {
		state.alertsView.typesIds = [...payload];
	},
	SET_CURRENT_ALERTSVIEW_FILTERS(state, payload) {
		state.alertsView.currentFilters = {
			typesIds: payload.typesIds,
			statusesIds: payload.statusesIds,
			dateRange: payload.dateRange,
		};
	},
	UPDATE_ALERTSVIEW_STATUSESIDS(state, payload) {
		state.alertsView.statusesIds = [...payload];
	},
	UPDATE_ALERTSVIEW_DATERANGE(state, payload) {
		state.alertsView.dateRange = payload;
	},

	// loans
	GET_ALERT_STATUS_TOTALS(state, payload) {
		state.alerts.alertStatusTotals = payload;
	},

	// contacts filter
	UPDATE_SELECTEDCONTACTSIDS(state, payload) {
		state.selectedContactsIds = [...payload];
	},
	UPDATE_SELECTEDCONTACTSIDS_UPDATED(state, payload) {
		state.selectedContactIdsUpdated = payload;
	},
};

const actions = {
	resetClientConnectAlerts({ commit }, resetCurrentFilterState = false) {
		commit('RESET_CLIENT_CONNECT_ALERTS', resetCurrentFilterState);
	},
	clearClientConnectAlerts({ commit }) {
		commit('CLEAR_CLIENT_CONNECT_ALERTS');
	},

	// Alerts
	async getClientConnectAlerts({ commit }, { payload, bySearchTerm }) {
		try {
			const response = await apiClient().post('/Alert/Search', payload);
			if (response && response.data && bySearchTerm) {
				commit('GET_CLIENT_CONNECT_ALERTS_BY_SEARCH_SUCCESS', response.data);
			} else if (response && response.data) {
				commit('GET_CLIENT_CONNECT_ALERTS_SUCCESS', response.data);
			}
		} catch (error) {
			return Promise.reject(error);
		} finally {
			commit('UPDATE_SELECTEDCONTACTSIDS_UPDATED', false);
		}
	},
	async getDashboardAlerts({ commit, getters }, statusesIds) {
		const { entityId, contactsIds } = getters.currentUserRolodexIds;
		const payload = {
			data: {
				entityId,
				contactsIds,
				typesIds: [],
				statusesIds,
			},
			pageNumber: 1,
			pageSize: 1,
			dateRange: -30,
		};
		try {
			const response = await apiClient().post('/Alert/Search', payload);
			if (
				response &&
				response.data &&
				statusesIds.includes(STATUS_ACTION_TYPES.NEW)
			) {
				commit('GET_DASHBOARD_TO_DO_ALERTS_SUCCESS', response.data.items);
			} else if (
				response &&
				response.data &&
				statusesIds.includes(STATUS_ACTION_TYPES.DONE_FOR_YOU)
			) {
				commit(
					'GET_DASHBOARD_DONE_FOR_YOU_ALERTS_SUCCESS',
					response.data.items,
				);
			}
		} catch (error) {
			return Promise.reject(error);
		}
	},
	async getAlertDetailsById({ commit }, { alertId }) {
		try {
			const response = await apiClient().get(
				`/Alert/GetAlert?alertId=${alertId}`,
			);
			if (response && response.data) {
				return Promise.resolve(response.data);
			}
		} catch (error) {
			return Promise.reject(error);
		}
	},
	async getAlertStatuses({ commit }) {
		try {
			const response = await apiClient().get('/Alert/GetAllStatuses');
			if (response && response.data) {
				commit('GET_ALERT_STATUSES_SUCCESS', response.data);
			}
		} catch (error) {
			return Promise.reject(error);
		}
	},
	async getAlertTypes({ commit }) {
		try {
			const data = await alertsService.getAlertTypes();
			commit('GET_ALERT_TYPES_SUCCESS', data);
		} catch (error) {
			return Promise.reject(error);
		}
	},

	storeCurrentAlertsViewFilters({ commit }, payload) {
		commit('SET_CURRENT_ALERTSVIEW_FILTERS', payload);
	},
	updateAlertsViewTypesIds({ commit }, typesIds) {
		if (typesIds === undefined) {
			commit('UPDATE_ALERTSVIEW_TYPESIDS', []);
		} else {
			commit('UPDATE_ALERTSVIEW_TYPESIDS', typesIds);
		}
	},
	updateAlertsViewStatusesIds({ commit }, statusesIds) {
		if (statusesIds === undefined) {
			commit('UPDATE_ALERTSVIEW_STATUSESIDS', []);
		} else {
			commit('UPDATE_ALERTSVIEW_STATUSESIDS', statusesIds);
		}
	},
	updateAlertsViewDateRange({ commit }, dateRange) {
		commit('UPDATE_ALERTSVIEW_DATERANGE', dateRange);
	},

	async getAlertStatusTotals({ commit }, payload) {
		try {
			const response = await apiClient().post('/Alert/GetCount', payload);
			if (response && response.data) {
				commit('GET_ALERT_STATUS_TOTALS', response.data);
			}
		} catch (error) {
			return Promise.reject(error);
		}
	},

	// contacts filter
	updateSelectedContactsIds({ commit, state }, selectedContactsIds) {
		if (!isEqual(state.selectedContactsIds, selectedContactsIds)) {
			commit('UPDATE_SELECTEDCONTACTSIDS', selectedContactsIds);
			commit('UPDATE_SELECTEDCONTACTSIDS_UPDATED', true);
		}
	},
	updateSelectedContactsIdsUpdated({ commit }, payload) {
		commit('UPDATE_SELECTEDCONTACTSIDS_UPDATED', payload);
	},
};

export default {
	state,
	mutations,
	actions,
};
